<!-- src/views/Dashboard/VizPanel.vue -->
<template>
  <div class="space-y-5">
    <!-- 1행: 채널별 추이 -->
    <div class="grid grid-cols-1 md:grid-cols-12 gap-5">

      <!-- 채널 추이 차트 -->
      <div class="md:col-span-4 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-chart-timeline-variant text-violet-500"></i>
          <span>채널별 추이</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0">
            <canvas ref="chartLineRef" class="w-full h-full"></canvas>
          </div>
        </div>
        <div class="mt-2.5 flex items-center gap-2 text-xs text-gray-400">
          <button @click="resetLineZoom(1)" type="button" class="px-2.5 py-1 border border-gray-200 rounded-md hover:bg-gray-50 active:bg-gray-100 text-gray-500 transition-colors">줌 리셋</button>
          <span>휠: 확대 · Shift+드래그: 이동</span>
        </div>
      </div>

      <!-- 채널 테이블 -->
      <div class="md:col-span-2 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-format-list-bulleted text-violet-500"></i>
          <span>채널 현황</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0 overflow-auto viz-table-wrap">
            <table class="viz-table">
              <thead>
                <tr>
                  <th class="text-left">채널</th>
                  <th class="text-right">게시물</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in channelTable" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right tabular-nums">{{ row.count?.toLocaleString?.() ?? row.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 키워드 추이 차트 -->
      <div class="md:col-span-4 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-chart-line text-violet-500"></i>
          <span>키워드별 추이</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0">
            <canvas ref="chartLine2Ref" class="w-full h-full"></canvas>
          </div>
        </div>
        <div class="mt-2.5 flex items-center gap-2 text-xs text-gray-400">
          <button @click="resetLineZoom(2)" type="button" class="px-2.5 py-1 border border-gray-200 rounded-md hover:bg-gray-50 active:bg-gray-100 text-gray-500 transition-colors">줌 리셋</button>
          <span>휠: 확대 · Shift+드래그: 이동</span>
        </div>
      </div>

      <!-- 키워드 카운트 테이블 -->
      <div class="md:col-span-2 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-counter text-violet-500"></i>
          <span>키워드 현황</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0 overflow-auto viz-table-wrap">
            <table class="viz-table">
              <thead>
                <tr>
                  <th class="text-left">채널</th>
                  <th class="text-right">게시물</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in countsTable" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right tabular-nums">{{ row.count?.toLocaleString?.() ?? row.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- 2행: 워드클라우드 + 키워드 테이블 -->
    <div class="grid grid-cols-1 md:grid-cols-10 gap-5">

      <!-- 워드클라우드 -->
      <div class="md:col-span-7 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-cloud-outline text-teal-500"></i>
          <span>워드클라우드</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0">
            <canvas ref="chartWordRef" class="w-full h-full" v-show="!wcError"></canvas>
            <div v-if="wcError" class="h-full w-full bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 text-sm">
              <div class="text-center">
                <i class="mdi mdi-cloud-off-outline text-3xl block mb-2"></i>
                워드클라우드를 배치할 수 없습니다.
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 키워드 순위 테이블 -->
      <div class="md:col-span-3 viz-card">
        <div class="viz-card-header">
          <i class="mdi mdi-trophy-outline text-amber-500"></i>
          <span>키워드 순위</span>
        </div>
        <div class="flex flex-col" :style="{ height: heightPx + 'px' }">
          <div class="flex-1 min-h-0 overflow-auto viz-table-wrap">
            <table class="viz-table">
              <thead>
                <tr>
                  <th class="text-left">키워드</th>
                  <th class="text-right">게시물</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in keywordTable" :key="row.keyword">
                  <td>{{ row.keyword }}</td>
                  <td class="text-right tabular-nums">{{ row.count?.toLocaleString?.() ?? row.count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import Chart from 'chart.js/auto'
import { WordCloudController, WordElement } from 'chartjs-chart-wordcloud'
import zoomPlugin from 'chartjs-plugin-zoom'
Chart.register(WordCloudController, WordElement, zoomPlugin)

const props = defineProps({
  lineLabels:     { type: Array,  required: true },
  lineDatasets:   { type: Array,  required: true },
  line2Labels:    { type: Array,  required: true },
  line2Datasets:  { type: Array,  required: true },
  channelTable:   { type: Array,  required: true },
  keywordTable:   { type: Array,  required: true },
  countsTable:   { type: Array,  required: true },
  wcLabels:       { type: Array,  required: true },
  wcWeights:      { type: Array,  required: true },
  height:         { type: Number, default: 320 }
})
const heightPx = props.height

const emit = defineEmits(['charts-ready'])

const chartLineRef = ref(null)
const chartLine2Ref = ref(null)
const chartWordRef = ref(null)
let lineChart = null
let lineChart2 = null
let wordChart = null
const wcError = ref(false)

function normalizeWeights(values, min=10, max=80) {
  if (!values?.length) return []
  const lo = Math.min(...values), hi = Math.max(...values)
  if (!isFinite(lo) || !isFinite(hi)) return values.map(() => Math.round((min+max)/2))
  if (hi === lo) return values.map(() => Math.round((min+max)/2))
  // 제곱근 스케일링으로 상위 키워드가 더 크게 보이도록 함
  const range = hi - lo
  return values.map(v => {
    const ratio = (v - lo) / range
    const scaled = Math.sqrt(ratio)  // 제곱근으로 차이 강조
    return Math.round(min + scaled * (max - min))
  })
}

const PALETTE_15 = [
  '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#ea580c',
  '#0ea5e9', '#22c55e', '#ef4444', '#8b5cf6', '#f59e0b',
  '#0891b2', '#10b981', '#f97316', '#6366f1', '#14b8a6'
]

const MAX_WORDS = 120
const wcTop = computed(() => {
  const labels = props.wcLabels?.slice?.(0, MAX_WORDS) ?? []
  const weights = props.wcWeights?.slice?.(0, MAX_WORDS) ?? []
  return { labels, weights }
})

function destroyCharts() {
  if (lineChart)  { lineChart.destroy();  lineChart  = null }
  if (lineChart2) { lineChart2.destroy(); lineChart2 = null }
  if (wordChart)  { wordChart.destroy();  wordChart  = null }
}

function buildLineChart(canvas, labels, datasets) {
  return new Chart(canvas, {
    type: 'line',
    data: {
      labels,
      datasets: (datasets ?? []).slice(0, 15).map((ds, idx) => {
        const color = PALETTE_15[idx]
        const soft  = color.length === 7 ? `${color}1A` : color
        const hover = color
        const safeData = (ds.data ?? []).map(v => {
          const n = Number(v)
          return Number.isFinite(n) ? n : null
        })
        return {
          ...ds,
          data: safeData,
          borderColor: color,
          backgroundColor: 'transparent',
          pointBackgroundColor: color,
          pointBorderColor: soft,
          pointHoverBackgroundColor: hover,
          pointHoverBorderColor: soft,
          borderWidth: 2,
          pointRadius: 2.5,
          pointHoverRadius: 5,
          spanGaps: true,
          tension: 0.3,
          borderDash: (ds.label ? String(ds.label).length : (idx+1)) % 5 === 0 ? [6, 3] : undefined
        }
      })
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false },
      plugins: {
        legend: { position: 'bottom', labels: { font: { size: 12 }, usePointStyle: true } },
        tooltip: {
          backgroundColor: '#111827',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 10,
          callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue}` }
        },
        zoom: {
          zoom: { wheel: { enabled: true }, pinch: { enabled: true }, drag: { enabled: true }, mode: 'x' },
          pan:  { enabled: true, mode: 'x', modifierKey: 'shift' },
          limits: { x: { min: 'original', max: 'original' }, y: { min: 'original', max: 'original' } }
        }
      },
      scales: {
        x: { ticks: { maxRotation: 45, minRotation: 25 }, grid: { drawOnChartArea: false } },
        y: { beginAtZero: true, grid: { color: '#e5e7eb' } }
      }
    }
  })
}

async function renderCharts() {
  destroyCharts()

  // 라인차트 2개 생성
  lineChart  = buildLineChart(chartLineRef.value,  props.lineLabels,  props.lineDatasets)
  lineChart2 = buildLineChart(chartLine2Ref.value, props.line2Labels, props.line2Datasets)

  // 워드클라우드
  wcError.value = false
  const labelsRaw = wcTop.value.labels
  const weightsRaw = wcTop.value.weights
  const weightsNorm = normalizeWeights(weightsRaw, 10, 80)

  const buildWord = (lbls, wts, pad=2) => new Chart(chartWordRef.value, {
    type: 'wordCloud',
    data: { labels: lbls, datasets: [{ data: wts }] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      devicePixelRatio: 2,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: '#111827',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          callbacks: {
            label(ctx) {
              const i = ctx.dataIndex
              const raw = weightsRaw[i] ?? ctx.raw
              return ` ${ctx.label}: ${raw?.toLocaleString?.() ?? raw}`
            }
          }
        }
      },
      elements: {
        word: {
          padding: pad,
          rotate(ctx) {
            const i = ctx.dataIndex
            // 인덱스 기반 고정 회전 (클릭/호버 시 변하지 않음)
            if (i % 7 === 0) return i % 2 === 0 ? -20 : 20
            return 0
          },
          spiral: 'archimedean',
          fontFamily: "Inter, Pretendard, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, 'Noto Sans KR', sans-serif",
          color(ctx) {
            const i = ctx.dataIndex
            const w = wts[i] ?? 20
            const hue = (i * 33) % 360
            // 큰 키워드일수록 더 진하고 채도 높게
            const sat = 60 + Math.round((w - 10) * (30 / (80 - 10)))
            const light = 55 - Math.round((w - 10) * (30 / (80 - 10)))
            return `hsl(${hue} ${sat}% ${light}%)`
          },
          hoverColor(ctx) {
            const i = ctx.dataIndex
            const w = wts[i] ?? 20
            const hue = (i * 33) % 360
            const sat = 80
            const light = 50 + Math.round((w - 12) * (10 / (34 - 12)))
            return `hsl(${hue} ${sat}% ${light}%)`
          }
        }
      },
      animation: false
    }
  })

  try {
    wordChart = buildWord(labelsRaw, weightsNorm, 2)
  } catch (e1) {
    try {
      const cut = 80
      wordChart = buildWord(labelsRaw.slice(0, cut), weightsNorm.slice(0, cut), 1)
    } catch (e2) {
      try {
        const cut = 60
        wordChart = buildWord(labelsRaw.slice(0, cut), weightsNorm.slice(0, cut), 1)
      } catch (e3) {
        wcError.value = true
      }
    }
  }

  // 차트 렌더링 완료 대기 후 이벤트 발생
  await nextTick()
  // 추가 프레임 대기로 차트가 완전히 그려지도록 보장
  await new Promise(resolve => requestAnimationFrame(() => {
    requestAnimationFrame(resolve)
  }))
  emit('charts-ready')
}

function resetLineZoom(which = 1) {
  if (which === 1) {
    if (lineChart?.resetZoom) lineChart.resetZoom()
  } else {
    if (lineChart2?.resetZoom) lineChart2.resetZoom()
  }
}

onMounted(async () => {
  await nextTick()
  if (chartWordRef.value && chartWordRef.value.clientWidth < 50) {
    await new Promise(r => requestAnimationFrame(r))
  }
  renderCharts()
})
onBeforeUnmount(() => destroyCharts())

watch(
  () => [
    props.lineLabels, props.lineDatasets,
    props.line2Labels, props.line2Datasets,
    props.wcLabels, props.wcWeights,
    props.height
  ],
  async () => {
    await nextTick()
    renderCharts()
  },
  { deep: true }
)
</script>

<style scoped>
.viz-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 16px;
  transition: box-shadow 0.2s;
}
.viz-card:hover {
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}
.viz-card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f5f5f5;
}
.viz-card-header i {
  font-size: 16px;
}
.viz-table-wrap {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}
.viz-table {
  width: 100%;
  font-size: 13px;
  border-collapse: collapse;
}
.viz-table thead tr {
  background: #fafafa;
}
.viz-table th {
  padding: 8px 12px;
  font-weight: 500;
  color: #6b7280;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: #fafafa;
  z-index: 1;
}
.viz-table td {
  padding: 7px 12px;
  color: #374151;
  border-bottom: 1px solid #f9f9f9;
}
.viz-table tbody tr:hover {
  background: #f8fafc;
}
.viz-table tbody tr:last-child td {
  border-bottom: none;
}
</style>
